import { apiGet, apiPost } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

export type NewsletterAction = "alta" | "baja";

export interface NewsletterRequestResponse {
  id: number;
  email: string;
  action: NewsletterAction;
  status: string;
  inserted_at: string;
}

export interface NewsletterRequestItem {
  id: number;
  email: string;
  action: NewsletterAction;
  status: string;
  inserted_at: string;
  processed_at: string | null;
}

export interface NewsletterRequestListResponse {
  total: number;
  items: NewsletterRequestItem[];
}

export function postNewsletterRequest(
  email: string,
  action: NewsletterAction,
): Promise<NewsletterRequestResponse> {
  return apiPost<NewsletterRequestResponse>("/newsletter", { email, action });
}

export function getNewsletterPreview(
  startDate: string,
  endDate: string,
): Promise<NewsletterRequestListResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  return apiGet<NewsletterRequestListResponse>(`/admin/newsletter/requests?${params}`);
}

async function parseDownloadError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text) as { detail?: string };
    if (body.detail) return body.detail;
  } catch {
    if (text.includes("newsletter_requests") && text.includes("does not exist")) {
      return "La tabla newsletter_requests no existe. Ejecuta: make migrate";
    }
    if (text) return text.slice(0, 200);
  }
  return res.statusText || "Request failed";
}

export async function downloadNewsletterCsv(
  startDate: string,
  endDate: string,
): Promise<void> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
  });
  const res = await fetch(`${API_BASE}/admin/newsletter/export?${params}`, {
    method: "GET",
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error(await parseDownloadError(res));
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition");
  const filename =
    disposition?.match(/filename="([^"]+)"/)?.[1] ??
    `newsletter_${startDate}_${endDate}.csv`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
